<template>
  <div class="relative border border-border-normal rounded-2xl">
    <!-- HEADER -->
    <div class="flex items-center justify-between p-2 pl-4 border-b border-border-normal">
      <!-- (LEFT SIDE) -->
      <div class="w-full flex items-center gap-2">
        <!-- Avatar -->
        <img v-if="data?.avatar" :src="data?.avatar" class="w-5 h-5  rounded-md">
        <!-- Fallback Avatar (initials) -->
        <div v-else class="w-5 h-5 bg-neutral-400 flex items-center justify-center text-background-hover p-1" style="border-radius: 4px;">
          <BaseText size="xs">
            {{ getIsPrivateMode ? '••' : initialName }}
          </BaseText>
        </div>
        <!-- Lens Name -->
        <BaseText type="label" size="sm">
          {{ getIsPrivateMode ? '•••••' : statusData.lens_name }}
        </BaseText>
        <!-- Guest Access Indicator -->
        <div v-if="statusData?.access_type === 'guest'" class="flex items-center p-0.5 rounded-md bg-neutral-25 cursor-default">
          <GuestAccessIcon class="text-icon-normal" />
          <BaseText type="label" size="sm" class="px-1 text-text-normal">
            Guest Access
          </BaseText>
        </div>
        <!-- Report Count -->
        <div v-if="reportCount === null" class="skeleton w-20 h-5 rounded-md" />
        <BaseText v-else size="sm" class="text-text-normal">
          {{ reportCount }} reports
        </BaseText>
      </div>
      <!-- (RIGHT SIDE) -->
      <div class="flex items-center gap-2 flex-shrink-0">
        <!-- Settings Button -->
        <button class="px-2 pr-2.5 py-1.5 rounded-md hover:bg-background-normal transition-colors flex items-center gap-1.5"
        @click="handleShowLensSettings">
          <SettingsIcon class="text-icon-normal" />
          <BaseText type="label" size="sm" class="text-text-muted">
            Settings
          </BaseText>
        </button>
        <!-- Issues Warning -->
        <button v-if="!loading && hasIssues" class="flex items-center gap-1.5 pl-2 py-1.5 pr-2.5 rounded-md bg-secondary-red-10"
        @click="handleShowLensSettings">
          <WarningIcon class="text-secondary-red-300" />
          <BaseText type="label" size="sm" class="text-secondary-red-300">
            Issues
          </BaseText>
        </button>
        <!-- Account Selector -->
        <OverviewAccountSelector 
          class="flex-shrink-0"
          :status-data="statusData"
          :platform="platform"
          :selected-account-id="selectedAdAccountId"
          @select="handleSelectAdAccount($event)"
        />
        <!-- View Button -->
        <router-link :style="{ pointerEvents: disableLensNav ? 'none' : 'auto' }"
        :to="{ name: 'LensDashboardView', params: { lensId: statusData.lens_id } }" class="flex-shrink-0">
          <div class="submit-btn px-2 py-1.5 rounded-md flex items-center gap-1.5" :class="{ 'disabled': disableLensNav }">
            <BaseText type="label" size="sm">
              Open Lens
            </BaseText>
            <OpenExternalIcon />
          </div>
        </router-link>
      </div>
    </div>
    <!-- NO AD ACCOUNTS FOR PLATFORM STATE -->
    <div v-if="!selectedAdAccountId" class="w-full flex flex-col gap-5 items-center justify-center fade-in" style="height: 260px"
    key="no-ad-accounts-for-platform-state">
      <component :is="platformMonoIcon" class="text-icon-normal" />
      <div class="flex flex-col gap-1 items-center text-center" style="max-width: 268px">
        <BaseText type="label" size="md" class="text-text-muted">
          No {{ formattedPlatformName }} Accounts
        </BaseText>
        <BaseText type="body" size="sm" class="text-text-normal">
          There are no {{ formattedPlatformName }} ad accounts assigned to this Lens brand.
        </BaseText>
      </div>
      <button class="flex items-center gap-1.5 pl-1.5 pr-2.5 py-1.5 rounded-md transition-colors duration-100 hover:bg-neutral-25 w-max"
      @click="handleShowLensSettings">
        <ConnectDataSourceIcon class="text-icon-normal" />
        <BaseText type="label" size="sm" class="text-text-muted">
          Connect {{ formattedPlatformName }} to {{ statusData.lens_name }}
        </BaseText>
      </button>
    </div>
    <!-- LOADING SKELETON -->
    <div v-else-if="loading" key="loading-skeleton">
      <LensOverviewCardSkeleton :lens-id="statusData.lens_id" />
    </div>
    <!-- AD ACCOUNT DISCONNECTED STATE -->
    <div v-else-if="status?.adAccountDisconnected" style="height: 260px" class="fade-in" key="ad-account-disconnected-state">
      <div class="flex flex-col items-center justify-center h-full">
        <BaseText type="label" size="md" class="text-text-muted mt-6">
          Ad Account Disconnected
        </BaseText>
        <BaseText type="body" size="sm" class="text-text-normal text-center mt-1">
          Looks like the {{ formattedPlatformName }} Ads Account for this Lens was disconnected.<br/>Just a quick reconnect and you'll be back in business!
        </BaseText>
        <BaseText type="label" size="sm" class="text-text-muted mt-3 mb-4">
          {{ currentAdAccountMeta?.account_name }} - {{ currentAdAccountMeta?.account_id?.replace('act_', '') }}
        </BaseText>
        <BaseButton class="mb-6" outlined @click="handleShowLensSettings">
          <div class="flex items-center gap-2">
            <component :is="platformMonoIcon" class="text-icon-normal" />
            <BaseText class="text-text-muted" type="label" size="sm">
              Connect Again
            </BaseText>
          </div>
        </BaseButton>
      </div>
    </div>
    <!-- BACKFILL LOADING STATE -->
    <div v-else-if="status.state === 'backfill' && !usableWithBackfill" class="flex fade-in" key="backfill-loading-state">
      <!-- Left Side -->
      <div style="width: 375px; height: 275px;" class="flex flex-col justify-between relative overflow-hidden p-5 border-r border-border-normal">
        <!-- Background Image -->
        <div class="absolute -bottom-12 -right-20">
          <video src="https://pub-c8addb4f25584e7abb14a4f5da2bd112.r2.dev/Lens-Isometric.webm" class="h-52 object-cover"
          autoplay loop muted playsinline />
        </div>
        <!-- Top Section -->
        <div class="w-72">
          <BaseText type="label" size="md" class="text-text-muted mb-2">
            We're importing your data
          </BaseText>
          <BaseText type="body" size="sm" class="text-text-normal">
            Depending of your account size this could take 24-48 hours
          </BaseText>
        </div>
        <!-- Bottom Info -->
        <div class="flex">
          <BaseButton text icon-only size="sm" @click="showIntercom">
            <div class="flex items-center gap-1 pr-1">
              <CustomerSupportUserIcon class="text-icon-normal" />
              <BaseText type="label" size="sm" class="text-text-muted">
                Customer Support
              </BaseText>
            </div>
          </BaseButton>
        </div>
      </div>
      <!-- Right Side -->
      <div class="flex-1">
        <!-- Top Section -->
        <div class="flex items-center gap-2 h-1/2">
          <div class="w-1/2 p-5 border-r border-border-normal">
            <BaseText type="label" size="sm" class="text-text-muted mb-2">
              Status
            </BaseText>
            <BaseText type="heading" size="md" class="text-text-muted mb-3">
              Pulling Data
            </BaseText>
            <div>
              <BaseText type="body" size="sm" class="text-secondary-green-200 font-medium">
                <div class="flex items-center gap-1">
                  <CheckCircleIcon />
                  No Errors
                </div>
              </BaseText>
            </div>
          </div>
          <!-- Ai Processing -->
          <div class="w-1/2 px-5">
            <BaseText type="label" size="sm" class="text-text-normal mb-4">
              AI Processing
            </BaseText>
            <!-- Progress Bar -->
            <div class="w-full h-2 mb-5 bg-background-normal rounded-full">
              <div class="h-full bg-icon-muted rounded-full" :style="{ width: `${status.syncPercentage.toFixed(0)}%` }"/>
            </div>
            <BaseText type="body" size="sm" class="text-text-muted">
              <div class="flex items-center gap-1">
                <CubeIcon />
                Transcribing & Analyzing
              </div>
            </BaseText>
          </div>
        </div>
        <!-- Bottom Section -->
        <div class="h-1/2 flex flex-col p-5 border-t border-border-normal">
          <BaseText type="label" size="sm" class="text-text-normal mb-2">
            Data Import Progress <span class="text-text-muted">{{ status.syncPercentage.toFixed(0) }}%</span>
          </BaseText>
          <!-- Progress Bar -->
          <div class="w-full h-2 mt-4 bg-background-normal rounded-full">
            <div class="h-full bg-icon-muted rounded-full" :style="{ width: `${status.syncPercentage.toFixed(0)}%` }" />
          </div>
        </div>
      </div>
    </div>
    <!-- NO INSIGHTS DATA STATE -->
    <div v-else-if="noInsightsData" class="w-full flex flex-col justify-center gap-1 items-center overflow-hidden relative fade-in" style="height: 260px">
      <div class="relative">
        <div class="absolute top-0 left-0 w-full h-full bg-white bg-opacity-70" />
        <img class="opacity-30" src="../../../assets/icons/lens-orb-small.png" style="height: 80px;">
      </div>
      <BaseText type="label" class="text-text-muted mt-2">
        {{ noInsightsLabel }}
      </BaseText>
      <BaseText size="sm" class="text-text-normal text-center" style="max-width: 360px">
        We don't have any ad spend activity for this period. Try adjusting the date range to see more data.
      </BaseText>
      <div class="flex gap-2 items-center mt-4">
        <BaseButton text icon-only size="sm" @click="openKB">
          <div class="flex items-center gap-1 pr-1">
            <CustomerSupportUserIcon class="text-icon-normal" />
            <BaseText type="label" size="sm" class="text-text-muted">
              Knowledge Base
            </BaseText>
          </div>
        </BaseButton>
        <BaseButton text icon-only size="sm" @click="showIntercom">
          <div class="flex items-center gap-1 pr-1">
            <CustomerSupportUserIcon class="text-icon-normal" />
            <BaseText type="label" size="sm" class="text-text-muted">
              Customer Support
            </BaseText>
          </div>
        </BaseButton>
      </div>
    </div>
    <!-- STANDARD STATE (HAS INSIGHTS) -->
    <div v-else-if="!loading" class="relative grid grid-cols-12 fade-in" key="standard-state">
      <!-- Top Ads List -->
      <div v-if="lensInsightAds.length > 0" class="col-span-4 space-y-2 border-r border-border-normal px-4 py-3">
        <div v-for="(topAd,index) in lensInsightAds.slice(0, 3)" :key="index" class="p-1">
          <div class="flex justify-center items-center gap-3">
            <BaseText type="label" size="sm" class="text-text-muted">
              {{ index + 1 }}
            </BaseText>
            <div class="overflow-hidden rounded-md">
              <img v-if="topAd.ad_image_url" :src="topAd.ad_image_url" :style="{ filter: getIsPrivateMode ? 'blur(8px)' : 'none' }"
              class="w-16 h-16 object-cover rounded-md">
              <div v-else class="w-16 h-16 bg-neutral-25 rounded-md flex items-center justify-center text-icon-disabled">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M8.25 3.25C8.25 3.66421 8.58579 4 9 4C9.41421 4 9.75 3.66421 9.75 3.25H8.25ZM9.75 1.75C9.75 1.33579 9.41421 1 9 1C8.58579 1 8.25 1.33579 8.25 1.75H9.75ZM15.75 20.75C15.75 20.3358 15.4142 20 15 20C14.5858 20 14.25 20.3358 14.25 20.75H15.75ZM14.25 22.25C14.25 22.6642 14.5858 23 15 23C15.4142 23 15.75 22.6642 15.75 22.25H14.25ZM3.25 9.75C3.66421 9.75 4 9.41421 4 9C4 8.58579 3.66421 8.25 3.25 8.25V9.75ZM1.75 8.25C1.33579 8.25 1 8.58579 1 9C1 9.41421 1.33579 9.75 1.75 9.75V8.25ZM20.75 14.25C20.3358 14.25 20 14.5858 20 15C20 15.4142 20.3358 15.75 20.75 15.75V14.25ZM22.25 15.75C22.6642 15.75 23 15.4142 23 15C23 14.5858 22.6642 14.25 22.25 14.25V15.75ZM4.21967 5.28033C4.51256 5.57322 4.98744 5.57322 5.28033 5.28033C5.57322 4.98744 5.57322 4.51256 5.28033 4.21967L4.21967 5.28033ZM3.78033 2.71967C3.48744 2.42678 3.01256 2.42678 2.71967 2.71967C2.42678 3.01256 2.42678 3.48744 2.71967 3.78033L3.78033 2.71967ZM19.7803 18.7197C19.4874 18.4268 19.0126 18.4268 18.7197 18.7197C18.4268 19.0126 18.4268 19.4874 18.7197 19.7803L19.7803 18.7197ZM20.2197 21.2803C20.5126 21.5732 20.9874 21.5732 21.2803 21.2803C21.5732 20.9874 21.5732 20.5126 21.2803 20.2197L20.2197 21.2803ZM3.91421 15.9142L4.44454 15.3839L4.44454 15.3839L3.91421 15.9142ZM8.08579 20.0858L7.55546 20.6161H7.55546L8.08579 20.0858ZM20.0858 8.08579L20.6161 7.55546V7.55546L20.0858 8.08579ZM15.9142 3.91421L16.4445 3.38388V3.38388L15.9142 3.91421ZM13.0858 3.91421L12.5555 3.38388V3.38388L13.0858 3.91421ZM10.2197 5.71967C9.92678 6.01256 9.92678 6.48744 10.2197 6.78033C10.5126 7.07322 10.9874 7.07322 11.2803 6.78033L10.2197 5.71967ZM6.78033 11.2803C7.07322 10.9874 7.07322 10.5126 6.78033 10.2197C6.48744 9.92678 6.01256 9.92678 5.71967 10.2197L6.78033 11.2803ZM3.91421 13.0858L3.38388 12.5555H3.38388L3.91421 13.0858ZM17.2197 12.7197C16.9268 13.0126 16.9268 13.4874 17.2197 13.7803C17.5126 14.0732 17.9874 14.0732 18.2803 13.7803L17.2197 12.7197ZM20.0858 10.9142L20.6161 11.4445V11.4445L20.0858 10.9142ZM10.9142 20.0858L11.4445 20.6161L10.9142 20.0858ZM13.7803 18.2803C14.0732 17.9874 14.0732 17.5126 13.7803 17.2197C13.4874 16.9268 13.0126 16.9268 12.7197 17.2197L13.7803 18.2803ZM9.75 3.25V1.75H8.25V3.25H9.75ZM14.25 20.75V22.25H15.75V20.75H14.25ZM3.25 8.25H1.75V9.75H3.25V8.25ZM20.75 15.75H22.25V14.25H20.75V15.75ZM5.28033 4.21967L3.78033 2.71967L2.71967 3.78033L4.21967 5.28033L5.28033 4.21967ZM18.7197 19.7803L20.2197 21.2803L21.2803 20.2197L19.7803 18.7197L18.7197 19.7803ZM3.38388 16.4445L7.55546 20.6161L8.61612 19.5555L4.44454 15.3839L3.38388 16.4445ZM20.6161 7.55546L16.4445 3.38388L15.3839 4.44454L19.5555 8.61612L20.6161 7.55546ZM12.5555 3.38388L10.2197 5.71967L11.2803 6.78033L13.6161 4.44454L12.5555 3.38388ZM5.71967 10.2197L3.38388 12.5555L4.44454 13.6161L6.78033 11.2803L5.71967 10.2197ZM18.2803 13.7803L20.6161 11.4445L19.5555 10.3839L17.2197 12.7197L18.2803 13.7803ZM11.4445 20.6161L13.7803 18.2803L12.7197 17.2197L10.3839 19.5555L11.4445 20.6161ZM19.5555 8.61612C20.0436 9.10427 20.0436 9.89573 19.5555 10.3839L20.6161 11.4445C21.6901 10.3706 21.6901 8.6294 20.6161 7.55546L19.5555 8.61612ZM7.55546 20.6161C8.6294 21.6901 10.3706 21.6901 11.4445 20.6161L10.3839 19.5555C9.89573 20.0436 9.10427 20.0436 8.61612 19.5555L7.55546 20.6161ZM4.44454 15.3839C3.95639 14.8957 3.95639 14.1043 4.44454 13.6161L3.38388 12.5555C2.30994 13.6294 2.30994 15.3706 3.38388 16.4445L4.44454 15.3839ZM16.4445 3.38388C15.3706 2.30994 13.6294 2.30994 12.5555 3.38388L13.6161 4.44454C14.1043 3.95639 14.8957 3.95639 15.3839 4.44454L16.4445 3.38388Z" fill="currentColor" />
                </svg>
              </div>
            </div>
            <div class="flex-1 space-y-3 flex-shrink  min-w-0">
              <BaseText type="label" size="sm" class="text-text-muted truncate">
                {{ getIsPrivateMode ? '•••••••••••••' : topAd.ad_name }}
              </BaseText>
              <div class="flex items-center gap-3 flex-wrap">
                <div class="flex gap-1">
                  <BaseText size="sm" class="text-text-normal">
                    Spend
                  </BaseText>
                  <BaseText type="label" size="sm" class="text-text-loud">
                    {{ getIsPrivateMode ? '•••' : formatNumber(topAd.spend, 'currency') }}
                  </BaseText>
                </div>
                <div class="flex items-center gap-1">
                  <BaseText size="sm" class="text-text-normal">
                    ROAS
                  </BaseText>
                  <BaseText type="label" size="sm" class="text-text-loud">
                    {{ getIsPrivateMode ? '•••' : formatNumber(topAd.roas, 'decimal') }}
                  </BaseText>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="col-span-4 space-y-2 border-r border-border-normal px-4 py-3 flex items-center justify-center w-full h-full">
        <BaseText size="sm" class="text-text-normal max-w-xs text-center">
          No past ads found
        </BaseText>
      </div>
      <!-- Overview Stats Section -->
      <div class="col-span-8 flex flex-col">
        <div class="grid grid-cols-12">
          <div v-for="(stat, index) in lensStats" :key="index" class="col-span-4">
            <div class="h-28 gap-2 p-4  border-b border-border-normal" :class="index !== lensStats.length - 1 ? 'border-r' : ''">
              <div class="space-y-2">
                <div class="flex items-center gap-1">
                  <BaseText type="label" size="sm" class="text-text-normal whitespace-nowrap truncate">
                    {{ stat.title }}
                  </BaseText>
                  <div v-if="stat.tooltip" class="relative">
                    <InfoIcon class="cursor-pointer text-icon-normal tooltip-trigger" :width="20" :height="20" />
                    <!-- Info Tooltip -->
                    <transition>
                      <div class="tooltip-container text-center px-2 py-1 bg-neutral-800 rounded-md shadow-md cursor-default" style="width: 140px;">
                        <BaseText type="label" size="xs" class="text-white">
                          {{ stat.tooltip }}
                        </BaseText>
                      </div>
                    </transition>
                  </div>
                </div>
                <BaseText type="heading" class="text-text-muted">
                  {{ getIsPrivateMode ? '••••••' : stat.value }}
                </BaseText>
              </div>
              <div class="mt-3">
                <PercentageChangeIndicator :percent="stat.previousPeriod" />
              </div>
            </div>
          </div>
        </div>
        <!-- Benchmarks Overview Section -->
        <LensBenchmarkCard
          v-if="!loading && data"
          class="p-4"
          :data="data"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapMutations, mapGetters } from 'vuex'
import { reverseProviderKeyMap } from '@/utils/lens/dataSourceUtils'
import { formatNumberLabel } from '../../../utils/lens/formatNumberLabel'
import { createDateRangeQuery } from '../../../utils/lens/filters/filterQueryUtils'
import LensAPI from '@/api/lens'

// Components
import OverviewAccountSelector from './OverviewAccountSelector.vue'
import PercentageChangeIndicator from './../PercentageChangeIndicator.vue'
import LensBenchmarkCard from './../LensBenchmarkCard.vue'
import LensOverviewCardSkeleton from './LensOverviewCardSkeleton.vue'

// Icons
import InfoIcon from '../../globals/Icons/InfoSimpleIcon.vue'
import BenchmarkIcon from '../../globals/Icons/LensIcons/BenchmarkIcon.vue'
import SettingsIcon from '../../globals/Icons/SettingsIcons/SettingsIcon.vue'
import CustomerSupportUserIcon from '../../globals/Icons/SidebarIcons/user/CustomerSupportUserIcon.vue'
import StorageIcon from '../../globals/Icons/StorageIcon.vue'
import CubeIcon from '../../globals/Icons/CubeIcon.vue'
import CheckCircleIcon from '../../globals/Icons/CheckCircleIcon.vue'
import ConnectDataSourceIcon from '../../globals/Icons/ConnectDataSourceIcon.vue'
import WarningIcon from '../../globals/Icons/WarningIcon.vue'
import MonoMetaIcon from '../../globals/Icons/socials/mono/MonoMetaIcon.vue'
import MonoTikTokIcon from '../../globals/Icons/socials/mono/MonoTiktokIcon.vue'
import OpenExternalIcon from '../../globals/Icons/OpenExternalIcon.vue'
import GuestAccessIcon from '../../globals/Icons/LensIcons/GuestAccessIcon.vue'

export default {
  name: 'LensOverviewCard',
  components: {
    OverviewAccountSelector,
    PercentageChangeIndicator,
    LensBenchmarkCard,
    LensOverviewCardSkeleton,
    // Icons
    InfoIcon,
    BenchmarkIcon,
    SettingsIcon,
    CustomerSupportUserIcon,
    StorageIcon,
    CubeIcon,
    CheckCircleIcon,
    ConnectDataSourceIcon,
    WarningIcon,
    OpenExternalIcon,
    GuestAccessIcon
  },
  props: {
    platform: {
      type: String,
      default: 'meta'
    },
    statusData: {
      type: Object,
      default: () => {}
    },
    selectedDateRange: {
      type: Object,
      required: true
    },
    dateFilterLabel: {
      type: String,
      required: true
    }
    // data: {
    //   type: Object,
    //   default: () => {}
    // }
  },
  data () {
    const STAT_MAP = {
      spend: { title: 'Spend', tooltip: 'Total amount spent on ads in the past week', type: 'currency' },
      goal_metric: { title: '', tooltip: '', type: 'decimal' },
      creatives_count: { title: 'New Creative Velocity', tooltip: 'Number of creatives tested this week', type: 'decimal' }
    }
    const BENCHMARK_KEYS = ['cpa', 'cpm', 'cvr', 'roas', 'cpc']

    return {
      // Data extension maps / key refs
      STAT_MAP,
      BENCHMARK_KEYS,

      selectedAdAccountId: null,
      data: null,
      loading: true,
      reportCount: null,
    }
  },
  watch: {
    platform: {
      handler (newPlatform) {
        // When platform changes, we select the first ad account for that platform
        const platformAdAccounts = this.statusData.ad_accounts.filter(account => reverseProviderKeyMap[account.provider] === newPlatform)
        const adAccountId = platformAdAccounts.length
          ? platformAdAccounts[0].ad_account_id
          : null
        this.selectedAdAccountId = adAccountId
        this.fetchLensData(adAccountId)
      },
      immediate: true
    },
    selectedAdAccountId (accountId) {
      this.fetchLensData(accountId)
    },
    getSidebarLenses () {
      this.fetchLensData(this.selectedAdAccountId)
    },
    selectedDateRange () {
      this.fetchLensData(this.selectedAdAccountId)
    }
  },
  computed: {
    ...mapGetters('LensModule', ['getSidebarLenses', 'getLensConnectionsStatus']),
    ...mapGetters('MetricsModule', ['getMetricLookup']),
    ...mapGetters('MiscModule', ['getIsPrivateMode']),
    lens () {
      return this.getSidebarLenses?.find(lens => lens.id === this.statusData.lens_id)
    },
    currentAdAccountMeta () {
      return this.lens?.accounts?.find(account => account.account_id === this.selectedAdAccountId) ?? null
    },
    status () {
      const selectedAdAccount = this.statusData.ad_accounts?.find(account => account.ad_account_id === this.selectedAdAccountId) ?? null
      if (!selectedAdAccount) return null
      
      const { is_valid, data_accuracy } = selectedAdAccount
      const syncPercentage = getSyncPercentage(data_accuracy?.percent_diff)
      return {
        state: syncPercentage > 90 ? 'ready' : 'backfill',
        syncPercentage,
        adAccountDisconnected: !is_valid
      }
    },
    hasIssues () {
      return !this.getLensConnectionsStatus[this.statusData.lens_id]?.isConnected
    },
    lensStats () {
      if (!this.data?.insights) return []
      const statKeys = Object.keys(this.STAT_MAP)
      const { insights, goal_metric: goalMetric } = this.data
      const previousPeriod = insights.previous_period

      return statKeys.map((insightKey) => {
        const title = this.STAT_MAP[insightKey].title
        const tooltip = this.STAT_MAP[insightKey].tooltip
        const insightType = this.STAT_MAP[insightKey].type

        if (insightKey === 'goal_metric') {
          const goalMetricDetails = this.data?.goal_metric_details
          const goalMetricVal = insights?.[goalMetric]
          if (!goalMetricDetails) {
            // Fallback placeholder values
            return {
              title: 'Goal Metric --',
              tooltip: 'Edit your goal metric in Lens settings',
              value: 0,
              previousPeriod: 0
            }
          }

          const { name: goalMetricName, type: goalMetricType } = goalMetricDetails
          const prevGoalVal = previousPeriod?.[goalMetric]
          const periodDiff = prevGoalVal ? ((goalMetricVal - prevGoalVal) / prevGoalVal) * 100 : 0

          return {
            title: `Goal Metric ${goalMetricName}`,
            tooltip: 'Edit your goal metric in Lens settings',
            value: this.formatNumber(goalMetricVal ?? 0, goalMetricType),
            previousPeriod: periodDiff
          }
        }
        const metricVal = insights?.[insightKey]
        const prevMetricVal = previousPeriod?.[insightKey]
        const periodDiff = ((metricVal - prevMetricVal) / prevMetricVal) * 100
        return {
          title,
          tooltip,
          value: this.formatNumber(insights?.[insightKey], insightType) || 0,
          previousPeriod: periodDiff
        }
      })
    },
    lensInsightAds () {
      return this.data?.insights?.ads || []
    },
    usableWithBackfill () {
      // For a lens brand to be usable with backfill, it must have at least one ad account with >10% sync percentage
      return this.statusData.ad_accounts.some(account => getSyncPercentage(account.data_accuracy?.percent_diff) > 10)
    },
    noInsightsData () {
      return !this.data?.insights
    },
    disableLensNav () {
      return !this.usableWithBackfill
    },
    initialName () {
      const name = this.statusData?.lens_name
      if (!name) return ''
      const initials = name.split(' ').map(word => word[0]).join('').toUpperCase()
      return initials.substring(0, 2) // Limit to 2 characters
    },
    benchmarkCopy () {
      const gmv = this.data?.gmv ? (this.data.gmv >= 1000000
        ? `${(this.data.gmv / 1000000).toFixed(0).replace(/\.0$/, '')}M`
        : `${(this.data.gmv / 1000).toFixed(0).replace(/\.0$/, '')}K`)
        : ''
      const aov = this.data?.aov_min || null
      const niche = this.data?.niche ? `${this.data.niche.replace(/_/g, ' ')}` : ''
      const showAov = aov && !this.getIsPrivateMode
      const showGmv = gmv && !this.getIsPrivateMode

      const aovText = showAov ? `>$${aov} AOV` : ''
      const gmvText = showGmv ? `>$${gmv} GMV` : ''

      return `${niche}${showAov ? ', ' : ''} ${aovText}${showGmv ? ', ' : ''} ${gmvText}`
    },
    platformMonoIcon () {
      return this.platform === 'meta' ? MonoMetaIcon : MonoTikTokIcon
    },
    formattedPlatformName () {
      return this.platform === 'meta' ? 'Meta' : 'TikTok'
    },
    noInsightsLabel () {
      return this.selectedDateRange.isRelativeToToday
        ? `No insights in ${this.dateFilterLabel.toLowerCase()}`
        : `No insights from ${this.dateFilterLabel}`
    }
  },
  methods: {
    ...mapMutations('MiscModule', ['SET_SETTINGS_DRAWER_VIEW_MODE']),
    ...mapMutations('RouterModule', ['SET_CURRENT_ROUTE_ID']),
    async fetchLensData (adAccountId) {
      this.loading = true
      if (!this.lens ||!this.getSidebarLenses?.length) return
      try {
        const { data } = await LensAPI.Lens.getInsightByLens(this.statusData.lens_id, {
          platform: this.platform,
          adAccountIds: [adAccountId],
          dateQuery: createDateRangeQuery(this.selectedDateRange),
          attrWindow: {
            click: this.lens.click_attribution_window,
            view: this.lens.view_attribution_window
          }
        })
        this.data = data
        this.reportCount = data?.report_count ?? 0
        this.loading = false
      } catch (error) {
        console.error('Error fetching insights:', error)
        this.$showAlert({
          type: 'error',
          message: 'An unexpected error occurred'
        })
      }
    },
    handleSelectAdAccount (adAccount) {
      console.log('handleSelectAdAccount called with adAccount:', adAccount)
      this.selectedAdAccountId = adAccount.id
    },
    handleShowLensSettings () {
      const lensId = this.statusData.lens_id
      this.SET_SETTINGS_DRAWER_VIEW_MODE('lens')
      this.SET_CURRENT_ROUTE_ID(lensId)
    },
    formatNumber (value, type = 'decimal') {
      return formatNumberLabel(value, type, this.data?.currency)
    },
    openKB () {
      window.open('https://foreplay.featurebase.app/help/collections/4789522-lens', '_blank')
    },
    showIntercom () {
      window.Intercom('show')
    }
  }
}

const getSyncPercentage = (percentDiff) => {
  return 100 - Math.min(percentDiff ?? 0, 100)
}
</script>

<style scoped>
.tooltip-container{
  pointer-events: none;
  position: absolute;
  bottom: calc(100% + 4px);
  left: 50%;
  transform: translateX(-50%) translateY(0);
  opacity: 0;
}
.tooltip-trigger:hover + .tooltip-container{
  opacity: 1
}

.v-enter-active, .v-leave-active {
  transition: opacity 150ms ease-in-out;
}
.v-enter-from, .v-enter, .v-leave-to {
  opacity: 0;
}
.v-enter-to, .v-leave-from {
  opacity: 1;
}

@property --submit-btn-color {
  syntax: '<color>';
  initial-value: #121212;
  inherits: false;
}
@property --submit-btn-opacity-1 {
  syntax: '<number>';
  initial-value: 0.12;
  inherits: false;
}
@property --submit-btn-opacity-2 {
  syntax: '<number>';
  initial-value: 0.12;
  inherits: false;
}
.submit-btn {
  background-color: #F6F8FA;
  background: linear-gradient(180deg, rgba(255, 255, 255, var(--submit-btn-opacity-1)) 0%, rgba(255, 255, 255, var(--submit-btn-opacity-2)) 100%), var(--submit-btn-color);
  color: white;
  box-shadow: 0px -1px 12px 0px rgba(255, 255, 255, 0.12) inset, 0px 0px 0px 1px var(--submit-btn-color);
  transition: 
    box-shadow 100ms ease-in-out,
    color 100ms ease-in-out,
    --submit-btn-color 100ms ease-in-out,
    --submit-btn-opacity-1 100ms ease-in-out,
    --submit-btn-opacity-2 100ms ease-in-out
}
.submit-btn:hover {
  --submit-btn-opacity-1: 0.24;
  --submit-btn-opacity-2: 0;
}
.submit-btn.disabled, .submit-btn.disabled:hover {
  --submit-btn-color: #F6F8FA;
  --submit-btn-opacity-1: 0;
  --submit-btn-opacity-2: 0;
  color: #A4acb9;
  box-shadow: none;
}

.fade-in {
  animation: fadeIn 100ms ease-in-out;
}
@keyframes fadeIn {
  from { opacity: 0.2; }
  to { opacity: 1; }
}
</style>
